import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { PaginationQueryParamsSchema } from '@terra/shared-dto-types';
import {
  NetworkGuardrailCreateSchema,
  NetworkGuardrailUpdateSchema,
  NetworkGuardrailsReturnSchema,
  NetworkGuardrailValidationResponseSchema,
  NetworkNoteCreateSchema,
  NetworkNoteUpdateSchema,
  NetworkNotesReturnSchema,
  NetworkNoteValidationResponseSchema,
} from '@terra/shared-contracts-external-network';
import { z } from 'zod';
import { INTERNAL_NETWORK_PATH_NAME } from './internal-network.constants';
import {
  CreateTunnelEnrollmentBodySchema,
  CreateTunnelEnrollmentResponseSchema,
  DeployAndLinkHostBodySchema,
  LaunchTunnelEngineBodySchema,
  LinkHostBodySchema,
  TerminateTunnelEngineResponseSchema,
  TunnelEngineSessionSchema,
  TunnelEnrollmentListResponseSchema,
  TunnelEnrollmentSchema,
  TunnelJumpResultSchema,
  TunnelMeshStatusSchema,
  UnlinkHostBodySchema,
} from './internal-network-tunnel.schema';
import {
  AdGraphQuerySchema,
  AdGraphResponseSchema,
  AgentHeartbeatBodySchema,
  AgentHeartbeatResponseSchema,
  CreateAgentEnrollmentBodySchema,
  CreateAgentEnrollmentResponseSchema,
  CreateInternalCrownJewelSchema,
  CreateInternalOwnedPrincipalSchema,
  CreateOperationEvidenceUploadSchema,
  OperationEvidenceUploadResponseSchema,
  ConfirmOperationEvidenceUploadSchema,
  InternalEvidenceContentSchema,
  InternalEvidenceSchema,
  InternalNetworkAgentEnrollmentListResponseSchema,
  InternalNetworkAgentEnrollmentSchema,
  CreateInternalNetworkSchema,
  InternalCrownJewelSchema,
  InternalNetworkCrownJewelSuggestionsResponseSchema,
  InternalOwnedPrincipalSchema,
  DiscoveryActivityLogEntrySchema,
  InternalDiscoveryRunSchema,
  InternalNetworkDeploymentRequirementsSchema,
  InternalNetworkSensorReleaseListResponseSchema,
  InternalNetworkSensorReleaseSchema,
  EnableInternalNetworkC2KillSwitchSchema,
  InternalNetworkC2KillSwitchStatusSchema,
  RotateEnrollmentKeyResponseSchema,
  SensorReleaseDownloadBundleSchema,
  InternalNetworkListQuerySchema,
  InternalNetworkListResponseSchema,
  InternalNetworkSchema,
  InternalNetworkAgentArtifactsResponseSchema,
  InternalNetworkOnboardingPackageSchema,
  InternalNetworkDiscoveryReadinessSchema,
  InternalNetworkStatsSchema,
  InternalNetworkTechnologiesSummarySchema,
  InternalNetworkSignalTrendQuerySchema,
  InternalNetworkSignalTrendResponseSchema,
  InternalNetworkCredentialSchema,
  InternalNetworkCredentialListResponseSchema,
  CreateInternalNetworkCredentialSchema,
  UpdateInternalNetworkCredentialSchema,
  VerifyInternalNetworkCredentialResponseSchema,
  ScenarioListResponseSchema,
  ScenarioStatusSchema,
  SurfaceFindingsListQuerySchema,
  SurfaceFindingsListResponseSchema,
  SurfaceFindingsCountQuerySchema,
  SurfaceFindingsCountResponseSchema,
  InternalNetworkSignalListQuerySchema,
  InternalNetworkSignalListResponseSchema,
  InternalNetworkSignalsCountQuerySchema,
  InternalNetworkSignalsCountResponseSchema,
  InternalNetworkOperationListQuerySchema,
  InternalNetworkOperationListResponseSchema,
  InternalNetworkEdrEffectivenessSchema,
  PatchInternalNetworkOperationTaskEdrOutcomeSchema,
  InternalOperationTaskSchema,
  InternalNetworkGraphDataExportSchema,
  InternalNetworkDiscoveryDataResetResultSchema,
  InternalNetworkGraphDataDeletionResultSchema,
  InternalNetworkDataDeletionResultSchema,
  InternalNetworkArtifactDeletionResultSchema,
  UpdateInternalNetworkSchema,
  GetInternalNetworkAdEntityResponseSchema,
  GetInternalNetworkAdEntityRelationshipsQuerySchema,
  GetInternalNetworkAdEntityRelationshipsResponseSchema,
  BloodHoundCypherQueryBodySchema,
  BloodHoundCypherQueryResponseSchema,
  ExternalNetworkFullReportBodySchema,
  ExternalNetworkFullReportResponseSchema,
  GenerateInternalNetworkSignalsResponseSchema,
  ImportBloodhoundExportBodySchema,
  ImportBloodhoundExportResponseSchema,
  IngestInternalIntegrationSignalBodySchema,
  IngestInternalIntegrationSignalResponseSchema,
  RunInternalNetworkSignalAttackResponseSchema,
  RunInternalNetworkC2CommandBodySchema,
  RunInternalNetworkC2CommandResponseSchema,
} from './internal-network.schema';
import {
  ListInternalNetworkAdEntitiesQuerySchema,
  ListInternalNetworkAdEntitiesResponseSchema,
  ListAllInternalNetworkAdEntitiesQuerySchema,
  ListAllInternalNetworkAdEntitiesResponseSchema,
  SearchInternalNetworkAdEntitiesQuerySchema,
  SearchInternalNetworkAdEntitiesResponseSchema,
  LookupInternalNetworkAdEntitiesByObjectIdsBodySchema,
  LookupInternalNetworkAdEntitiesByObjectIdsResponseSchema,
} from './internal-network-ad-entity-list.schema';
import {
  IngestPasswordAuditResultsBodySchema,
  ListInternalNetworkHostsQuerySchema,
  ListInternalNetworkHostsResponseSchema,
  PasswordAuditJobListResponseSchema,
  PasswordAuditJobSchema,
  PasswordAuditSummarySchema,
  StartPasswordAuditBodySchema,
  UploadVriScanBodySchema,
  VriScanBatchListResponseSchema,
  VriScanBatchSchema,
} from './infra-pov.schema';

export const InternalNetworkContract = terraContract
  .prefix(`/${INTERNAL_NETWORK_PATH_NAME}`)
  .router({
    list: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List internal network surfaces',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: InternalNetworkListQuerySchema }))
      .output(InternalNetworkListResponseSchema),
    getById: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}',
        summary: 'Get internal network surface by ID',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(InternalNetworkSchema),
    getAgentArtifacts: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/agent-artifacts',
        summary: 'Get host agent artifact SHA-256 hashes for EDR registration',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(InternalNetworkAgentArtifactsResponseSchema),
    getOnboardingPackage: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/onboarding-package',
        summary: 'Get EDR onboarding package for an internal network surface',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(InternalNetworkOnboardingPackageSchema),
    create: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary: 'Create internal network surface',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: CreateInternalNetworkSchema }))
      .output(InternalNetworkSchema),
    update: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'PATCH',
        path: '/{networkId}',
        summary: 'Update internal network surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: UpdateInternalNetworkSchema,
        }),
      )
      .output(InternalNetworkSchema),
    delete: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}',
        summary: 'Delete internal network surface',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(z.object({ networkId: z.string() })),
    listSensorReleases: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/sensor-releases',
        summary: 'List internal network sensor releases',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: PaginationQueryParamsSchema }))
      .output(InternalNetworkSensorReleaseListResponseSchema),
    getLatestSensorRelease: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/sensor-releases/latest',
        summary: 'Get latest internal network sensor release',
        inputStructure: 'detailed',
      })
      .output(InternalNetworkSensorReleaseSchema.nullable()),
    getDeploymentRequirements: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/deployment-requirements',
        summary: 'Get sensor deployment outbound endpoint requirements',
        inputStructure: 'detailed',
      })
      .output(InternalNetworkDeploymentRequirementsSchema),
    getSensorReleaseDownloadBundle: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/sensor-releases/{version}/download-bundle',
        summary: 'Get presigned download URLs for sensor release artifacts',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ version: z.string() }) }))
      .output(SensorReleaseDownloadBundleSchema),
    createEnrollment: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/enrollments',
        summary: 'Create sensor enrollment for surface',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: CreateAgentEnrollmentBodySchema,
        }),
      )
      .output(CreateAgentEnrollmentResponseSchema),
    listEnrollments: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/enrollments',
        summary: 'List sensor enrollments for surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: PaginationQueryParamsSchema,
        }),
      )
      .output(InternalNetworkAgentEnrollmentListResponseSchema),
    revokeEnrollment: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/enrollments/{enrollmentId}',
        summary: 'Revoke sensor enrollment',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            enrollmentId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(InternalNetworkAgentEnrollmentSchema),
    rotateEnrollmentKey: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/enrollments/{enrollmentId}/rotate-key',
        summary: 'Rotate sensor enrollment access key',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            enrollmentId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(RotateEnrollmentKeyResponseSchema),
    createTunnelEnrollment: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/tunnel-enrollments',
        summary: 'Create cloud-tunnel subnet-router enrollment for surface',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: CreateTunnelEnrollmentBodySchema,
        }),
      )
      .output(CreateTunnelEnrollmentResponseSchema),
    reprovisionTunnelEnrollmentArtifacts: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/tunnel-enrollments/{enrollmentId}/artifacts',
        summary:
          'Regenerate subnet-router deployment artifacts for an existing tunnel enrollment',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            enrollmentId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(CreateTunnelEnrollmentResponseSchema),
    listTunnelEnrollments: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/tunnel-enrollments',
        summary: 'List cloud-tunnel enrollments for surface',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(TunnelEnrollmentListResponseSchema),
    revokeTunnelEnrollment: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/tunnel-enrollments/{enrollmentId}',
        summary: 'Revoke a cloud-tunnel enrollment',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            enrollmentId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(TunnelEnrollmentSchema),
    launchTunnelEngine: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/tunnel-engines',
        summary: 'Launch per-engagement cloud C2 engine over tenant tailnet',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: LaunchTunnelEngineBodySchema,
        }),
      )
      .output(TunnelEngineSessionSchema),
    terminateTunnelEngine: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/tunnel-engines/{operationId}',
        summary: 'Terminate a cloud C2 engine session',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            operationId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(TerminateTunnelEngineResponseSchema),
    deployAndLinkHost: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/tunnel-jumps/deploy-and-link',
        summary:
          'Deploy a host implant over the tunnel engine and parent-link it into the Mythic mesh',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: DeployAndLinkHostBodySchema,
        }),
      )
      .output(TunnelJumpResultSchema),
    linkHost: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/tunnel-jumps/link',
        summary: 'Parent-initiate a P2P link to a host implant',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: LinkHostBodySchema,
        }),
      )
      .output(TunnelJumpResultSchema),
    unlinkHost: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/tunnel-jumps/unlink',
        summary: 'Unlink a host implant from its Mythic P2P parent',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: UnlinkHostBodySchema,
        }),
      )
      .output(TunnelJumpResultSchema),
    listTunnelMesh: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/tunnel-mesh',
        summary: 'List tunnel engine/sensor binding and linked host implants',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
        }),
      )
      .output(TunnelMeshStatusSchema),
    recordHeartbeat: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_INTERNAL_NETWORK_SENSOR.name],
      })
      .route({
        method: 'POST',
        path: '/enrollments/{enrollmentId}/heartbeat',
        summary: 'Record sensor heartbeat',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ enrollmentId: z.string() }),
          body: AgentHeartbeatBodySchema,
        }),
      )
      .output(AgentHeartbeatResponseSchema),
    listDiscoveryRuns: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/discovery-runs',
        summary: 'List discovery runs for surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: PaginationQueryParamsSchema,
        }),
      )
      .output(
        z.object({
          data: z.array(InternalDiscoveryRunSchema),
          total: z.number(),
        }),
      ),
    listOperations: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/operations',
        summary: 'List remote execution operations for surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: InternalNetworkOperationListQuerySchema,
        }),
      )
      .output(InternalNetworkOperationListResponseSchema),
    getEdrEffectiveness: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/edr-effectiveness',
        summary: 'Get EDR effectiveness scoreboard for surface operations',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(InternalNetworkEdrEffectivenessSchema),
    patchOperationTaskEdrOutcome: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'PATCH',
        path: '/{networkId}/operations/{operationId}/tasks/{taskId}/edr-outcome',
        summary: 'Set EDR outcome for an operation task',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            operationId: z.string(),
            taskId: z.string(),
          }),
          body: PatchInternalNetworkOperationTaskEdrOutcomeSchema,
        }),
      )
      .output(InternalOperationTaskSchema),
    getEvidence: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/evidence/{evidenceId}',
        summary: 'Get internal network evidence content',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            evidenceId: z.string(),
          }),
        }),
      )
      .output(InternalEvidenceContentSchema),
    createOperationEvidenceUpload: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/operations/{operationId}/evidence/upload',
        summary: 'Create presigned upload URL for operation evidence',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            operationId: z.string(),
          }),
          body: CreateOperationEvidenceUploadSchema,
        }),
      )
      .output(OperationEvidenceUploadResponseSchema),
    confirmOperationEvidenceUpload: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/operations/{operationId}/evidence/confirm',
        summary: 'Register uploaded operation evidence',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            operationId: z.string(),
          }),
          body: ConfirmOperationEvidenceUploadSchema,
        }),
      )
      .output(InternalEvidenceSchema),
    triggerDiscovery: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/discovery-runs',
        summary: 'Trigger AD discovery run',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(InternalDiscoveryRunSchema),
    stopDiscovery: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/discovery-runs/{discoveryRunId}/stop',
        summary: 'Stop an active AD discovery run',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            discoveryRunId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(InternalDiscoveryRunSchema),
    getDiscoveryActivityLogs: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/discovery-runs/{discoveryRunId}/activity-logs',
        summary: 'Get cached discovery activity logs',
        description:
          'Retrieves cached activity log entries for a discovery run (2-day TTL).',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            discoveryRunId: z.string(),
          }),
        }),
      )
      .output(DiscoveryActivityLogEntrySchema.array()),
    registerC2Callback: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_INTERNAL_NETWORK_SENSOR.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/enrollments/{enrollmentId}/c2-callback',
        summary: 'Register remote execution callback ID for sensor enrollment',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            enrollmentId: z.string(),
          }),
          body: z.object({
            c2AgentCallbackId: z.string().min(1),
          }),
        }),
      )
      .output(z.object({ ok: z.literal(true) })),
    listCrownJewels: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/crown-jewels',
        summary: 'List crown jewels for surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: PaginationQueryParamsSchema,
        }),
      )
      .output(
        z.object({
          data: z.array(InternalCrownJewelSchema),
          total: z.number(),
        }),
      ),
    createCrownJewel: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/crown-jewels',
        summary: 'Mark AD entity as crown jewel',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: CreateInternalCrownJewelSchema,
        }),
      )
      .output(InternalCrownJewelSchema),
    deleteCrownJewel: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/crown-jewels/{crownJewelId}',
        summary: 'Remove crown jewel designation from AD entity',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            crownJewelId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(z.object({ crownJewelId: z.string() })),
    getCrownJewelSuggestions: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/crown-jewels/suggestions',
        summary: 'List suggested crown jewel targets from BloodHound presets',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(InternalNetworkCrownJewelSuggestionsResponseSchema),
    listOwnedPrincipals: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/owned-principals',
        summary: 'List active owned principals for surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: PaginationQueryParamsSchema,
        }),
      )
      .output(
        z.object({
          data: z.array(InternalOwnedPrincipalSchema),
          total: z.number(),
        }),
      ),
    createOwnedPrincipal: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/owned-principals',
        summary: 'Mark AD entity as owned',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: CreateInternalOwnedPrincipalSchema,
        }),
      )
      .output(InternalOwnedPrincipalSchema),
    revokeOwnedByCredential: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/owned-principals/by-credential/{credentialId}',
        summary: 'Revoke owned principal linked to a credential',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            credentialId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(z.object({ credentialId: z.string() })),
    revokeOwnedPrincipal: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/owned-principals/{entityId}',
        summary: 'Revoke owned principal designation from AD entity',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            entityId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(z.object({ entityId: z.string() })),
    listScenariosBySignal: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/signals/{signalId}/scenarios',
        summary: 'List scenarios linked to a signal',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ signalId: z.string() }),
          query: PaginationQueryParamsSchema,
        }),
      )
      .output(ScenarioListResponseSchema),
    runScenario: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/scenarios/{scenarioId}/run',
        summary: 'Run an internal network attack scenario',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ scenarioId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(
        z.object({
          scenarioId: z.string(),
          status: ScenarioStatusSchema,
        }),
      ),
    stopScenario: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/scenarios/{scenarioId}/stop',
        summary: 'Stop a running internal network attack scenario',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ scenarioId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(
        z.object({
          scenarioId: z.string(),
          status: ScenarioStatusSchema,
        }),
      ),
    getGraph: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/graph',
        summary: 'Get AD discovery graph for surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: AdGraphQuerySchema,
        }),
      )
      .output(AdGraphResponseSchema),
    searchAdEntities: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/ad-entities/search',
        summary: 'Search AD entities for surface graph exploration',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: SearchInternalNetworkAdEntitiesQuerySchema,
        }),
      )
      .output(SearchInternalNetworkAdEntitiesResponseSchema),
    lookupAdEntitiesByObjectIds: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/ad-entities/lookup-by-object-ids',
        summary: 'Look up AD entities by BloodHound object IDs',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: LookupInternalNetworkAdEntitiesByObjectIdsBodySchema,
        }),
      )
      .output(LookupInternalNetworkAdEntitiesByObjectIdsResponseSchema),
    listAdEntities: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/ad-entities',
        summary: 'List AD entities by kind for surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: ListInternalNetworkAdEntitiesQuerySchema,
        }),
      )
      .output(ListInternalNetworkAdEntitiesResponseSchema),
    listAllAdEntities: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/ad-entities/all',
        summary: 'List AD entities across all kinds for surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: ListAllInternalNetworkAdEntitiesQuerySchema,
        }),
      )
      .output(ListAllInternalNetworkAdEntitiesResponseSchema),
    getAdEntity: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/ad-entities/{nodeId}',
        summary: 'Get AD entity details by node ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            nodeId: z.string(),
          }),
        }),
      )
      .output(GetInternalNetworkAdEntityResponseSchema),
    getAdEntityRelationships: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/ad-entities/{nodeId}/relationships',
        summary: 'Get AD entity relationships by node ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            nodeId: z.string(),
          }),
          query: GetInternalNetworkAdEntityRelationshipsQuerySchema,
        }),
      )
      .output(GetInternalNetworkAdEntityRelationshipsResponseSchema),
    runBloodhoundCypher: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/bloodhound/cypher',
        summary: 'Run a read-only BloodHound CE cypher query for a surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: BloodHoundCypherQueryBodySchema,
        }),
      )
      .output(BloodHoundCypherQueryResponseSchema),
    queryBloodhoundGraph: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/bloodhound/graph',
        summary: 'Query BloodHound AD attack-path graph for a surface',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: AdGraphQuerySchema,
        }),
      )
      .output(AdGraphResponseSchema),
    getStats: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/stats',
        summary: 'Get internal network surface KPI stats',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(InternalNetworkStatsSchema),
    getTechnologiesSummary: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/technologies-summary',
        summary: 'Get discovered services and products summary for a surface',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(InternalNetworkTechnologiesSummarySchema),
    getSignalsTrend: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/signals/trend',
        summary: 'Get daily signal counts for a surface over a time window',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: InternalNetworkSignalTrendQuerySchema,
        }),
      )
      .output(InternalNetworkSignalTrendResponseSchema),
    getDiscoveryReadiness: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/discovery-readiness',
        summary: 'Get authoritative discovery readiness for a surface',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(InternalNetworkDiscoveryReadinessSchema),
    listSignals: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/signals',
        summary: 'List internal network surface signals',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: InternalNetworkSignalListQuerySchema,
        }),
      )
      .output(InternalNetworkSignalListResponseSchema),
    countSignals: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/signals/count',
        summary: 'Count internal network surface signals',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: InternalNetworkSignalsCountQuerySchema,
        }),
      )
      .output(InternalNetworkSignalsCountResponseSchema),
    listFindings: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/findings',
        summary: 'List internal network surface findings',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: SurfaceFindingsListQuerySchema,
        }),
      )
      .output(SurfaceFindingsListResponseSchema),
    countFindings: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/findings/count',
        summary: 'Count internal network surface findings',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: SurfaceFindingsCountQuerySchema,
        }),
      )
      .output(SurfaceFindingsCountResponseSchema),
    createFullReport: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/full-report',
        summary: 'Create a full report for selected internal network findings',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: ExternalNetworkFullReportBodySchema,
        }),
      )
      .output(ExternalNetworkFullReportResponseSchema),
    generateSignals: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/signals/generate',
        summary: 'Trigger on-demand internal network signal generation',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(GenerateInternalNetworkSignalsResponseSchema),
    runSignalAttack: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/signals/{signalId}/run-attack',
        summary: 'Run attack for the primary scenario linked to a signal',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            signalId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(RunInternalNetworkSignalAttackResponseSchema),
    runC2Command: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/c2/run-command',
        summary:
          'Launch an allowlisted Mythic C2 command against the surface asynchronously',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: RunInternalNetworkC2CommandBodySchema,
        }),
      )
      .output(RunInternalNetworkC2CommandResponseSchema),
    importBloodhoundExport: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/import/bloodhound-export',
        summary:
          'Import a BloodHound CE collection zip from an external collector',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: ImportBloodhoundExportBodySchema,
        }),
      )
      .output(ImportBloodhoundExportResponseSchema),
    ingestIntegrationSignal: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/import/integration-signal',
        summary:
          'Ingest a third-party AD security signal (Defender, SIEM, etc.)',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: IngestInternalIntegrationSignalBodySchema,
        }),
      )
      .output(IngestInternalIntegrationSignalResponseSchema),
    listCredentials: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/credentials',
        summary: 'List internal network AD credentials',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: PaginationQueryParamsSchema,
        }),
      )
      .output(InternalNetworkCredentialListResponseSchema),
    createCredential: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/credentials',
        summary: 'Create internal network AD credential',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: CreateInternalNetworkCredentialSchema,
        }),
      )
      .output(InternalNetworkCredentialSchema),
    updateCredential: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'PATCH',
        path: '/{networkId}/credentials/{credentialId}',
        summary: 'Update internal network AD credential',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            credentialId: z.string(),
          }),
          body: UpdateInternalNetworkCredentialSchema,
        }),
      )
      .output(InternalNetworkCredentialSchema),
    deleteCredential: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/credentials/{credentialId}',
        summary: 'Delete internal network AD credential',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            credentialId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(z.object({ credentialId: z.string() })),
    verifyCredential: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/credentials/{credentialId}/verify',
        summary:
          'Verify internal network AD credential via cloud tunnel runner or online sensor',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            credentialId: z.string(),
          }),
          body: z.object({}).optional(),
        }),
      )
      .output(VerifyInternalNetworkCredentialResponseSchema),
    getNetworkGuardrails: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/guardrails',
        summary: 'List internal network surface guardrails',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(NetworkGuardrailsReturnSchema),
    createNetworkGuardrail: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/guardrails',
        summary: 'Create internal network surface guardrail',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: NetworkGuardrailCreateSchema,
        }),
      )
      .output(NetworkGuardrailValidationResponseSchema),
    updateNetworkGuardrail: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'PUT',
        path: '/{networkId}/guardrails/{guardrailId}',
        summary: 'Update internal network surface guardrail',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            guardrailId: z.string(),
          }),
          body: NetworkGuardrailUpdateSchema,
        }),
      )
      .output(NetworkGuardrailValidationResponseSchema),
    deleteNetworkGuardrail: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/guardrails/{guardrailId}',
        summary: 'Delete internal network surface guardrail',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            guardrailId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(z.object({ message: z.string() })),
    getNetworkNotes: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/notes',
        summary: 'List internal network surface testing instructions',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(NetworkNotesReturnSchema),
    createNetworkNote: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/notes',
        summary: 'Create internal network surface testing instruction',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: NetworkNoteCreateSchema,
        }),
      )
      .output(NetworkNoteValidationResponseSchema),
    updateNetworkNote: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'PUT',
        path: '/{networkId}/notes/{noteId}',
        summary: 'Update internal network surface testing instruction',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            noteId: z.string(),
          }),
          body: NetworkNoteUpdateSchema,
        }),
      )
      .output(NetworkNoteValidationResponseSchema),
    deleteNetworkNote: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/notes/{noteId}',
        summary: 'Delete internal network surface testing instruction',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            networkId: z.string(),
            noteId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(z.object({ message: z.string() })),
    getC2KillSwitchStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/c2-kill-switch',
        summary: 'Get tenant-wide internal network C2 emergency disable state',
        inputStructure: 'detailed',
      })
      .output(InternalNetworkC2KillSwitchStatusSchema),
    enableC2KillSwitch: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/c2-kill-switch/enable',
        summary: 'Enable tenant-wide internal network C2 emergency disable',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: EnableInternalNetworkC2KillSwitchSchema }))
      .output(InternalNetworkC2KillSwitchStatusSchema),
    disableC2KillSwitch: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/c2-kill-switch/disable',
        summary: 'Clear tenant-wide internal network C2 emergency disable',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: z.object({}) }))
      .output(InternalNetworkC2KillSwitchStatusSchema),
    exportGraphData: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/graph-data/export',
        summary:
          'Export normalized AD graph data for an internal network surface',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(InternalNetworkGraphDataExportSchema),
    deleteGraphData: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/graph-data',
        summary: 'Delete AD graph data for an internal network surface',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(InternalNetworkGraphDataDeletionResultSchema),
    deleteNetworkArtifacts: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/artifacts',
        summary: 'Delete stored artifacts for an internal network surface',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(InternalNetworkArtifactDeletionResultSchema),
    deleteGraphDataAndArtifacts: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'DELETE',
        path: '/{networkId}/compliance-data',
        summary:
          'Delete AD graph data and stored artifacts for an internal network surface',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(InternalNetworkDataDeletionResultSchema),
    resetDiscoveryData: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/reset-discovery-data',
        summary:
          'Reset discovery-derived data for an internal network surface while preserving surface config, enrollments, and credentials',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(InternalNetworkDiscoveryDataResetResultSchema),
    listNetworkHosts: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/hosts',
        summary: 'List first-seen surface hosts',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          query: ListInternalNetworkHostsQuerySchema,
        }),
      )
      .output(ListInternalNetworkHostsResponseSchema),
    startPasswordAudit: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/password-audits',
        summary: 'Queue a crack-only AD password audit job',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: StartPasswordAuditBodySchema,
        }),
      )
      .output(PasswordAuditJobSchema),
    listPasswordAudits: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/password-audits',
        summary: 'List AD password audit jobs',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(PasswordAuditJobListResponseSchema),
    getPasswordAudit: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/password-audits/{jobId}',
        summary: 'Get an AD password audit job and results',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string(), jobId: z.string() }),
        }),
      )
      .output(PasswordAuditJobSchema),
    getPasswordAuditSummary: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/password-audits/{jobId}/summary',
        summary: 'Get AD password audit job summary histograms',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string(), jobId: z.string() }),
        }),
      )
      .output(PasswordAuditSummarySchema),
    ingestPasswordAuditResults: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/password-audits/{jobId}/results',
        summary: 'Ingest precomputed password audit results',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string(), jobId: z.string() }),
          body: IngestPasswordAuditResultsBodySchema,
        }),
      )
      .output(PasswordAuditJobSchema),
    uploadVriScan: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'POST',
        path: '/{networkId}/vri/batches',
        summary: 'Upload a scanner CSV for vulnerability risk intelligence',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string() }),
          body: UploadVriScanBodySchema,
        }),
      )
      .output(VriScanBatchSchema),
    listVriBatches: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/vri/batches',
        summary: 'List VRI scan batches',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ networkId: z.string() }) }))
      .output(VriScanBatchListResponseSchema),
    getVriBatch: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_INTERNAL_NETWORK.name],
      })
      .route({
        method: 'GET',
        path: '/{networkId}/vri/batches/{batchId}',
        summary: 'Get a VRI scan batch and classifications',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ networkId: z.string(), batchId: z.string() }),
        }),
      )
      .output(VriScanBatchSchema),
  });
